import CustomButton from "components/common/Button/CustomButton";
import DateRangeWithRegister from "components/common/DateRangePicker/DateRangePickerWithRegister";
import ModalSkeleton from "components/Modals/ModalSkeleton";
import { apiPutRequest } from "helpers/Requests";
import { notifyPendingLeaveChanged } from "helpers/pendingLeaveBadge";
import React, { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";

const LeaveUpdateModal = (props) => {
  const {
    handleClose,
    leaveId,
    isUserGracePeriodOver,
    workerUuid,
    isOpen,
    onLeaveUpdated,
    leaveData, // 👈 pass the selected leave object here
  } = props;

  const [request, setRequest] = useState({
    isRequested: false,
    isSuccess: false,
    isFailed: false,
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    watch,
    control,
    reset,
    trigger,
  } = useForm();

  const [isLoading, setIsLoading] = useState(false);

  const startDate = watch("startDate");
  const endDate = watch("endDate");

  useEffect(() => {
    if (leaveData) {
      reset({
        startDate: leaveData.start_date || "",
        endDate: leaveData.end_date || "",
        details: leaveData.details || "",
      });
    }
  }, [leaveData, reset]);

   useEffect(() => {
    if (startDate && endDate) {
      trigger(["startDate", "endDate"]);
    }
  }, [startDate, endDate, trigger]);

  const handleUpdate = async (data) => {
    try {
      setRequest({ isRequested: true, isSuccess: false, isFailed: false });

    //   const payload = {
    //     start_date: data.startDate,
    //     end_date: data.endDate,
    //     details: data.details || null,
    //   };

      const response = await apiPutRequest(`/sp/worker-leave/${leaveId}`, {
        worker_uuid: workerUuid,
        start_date: data.startDate,
        end_date: data.endDate,
        details: data.details || null,
      });

      if (response?.status === 200) {
        setRequest({ isRequested: false, isSuccess: true, isFailed: false });
        notifyPendingLeaveChanged();
        if (onLeaveUpdated) onLeaveUpdated(); // refresh
        handleClose();
      }
    } catch (error) {
      console.error("Update Leave Error:", error);
      setRequest({ isRequested: false, isSuccess: false, isFailed: true });
    }
  };

  const modalFooter = () => (
    <>
      <CustomButton
        showLoading={isLoading}
        isDisabled={isLoading || isUserGracePeriodOver}
        label='Yes, update Leave'
        clickHandler={handleSubmit(handleUpdate)}
        variant='primary'
      />
      <CustomButton clickHandler={handleClose} label='No, cancel changes' />
    </>
  );

  return (
    <>
      {isOpen && (
        <ModalSkeleton
          title='Update Leave'
          cssClass='w-[50%]'
          isOpen={isOpen}
          closeModal={handleClose}
          modalFooter={modalFooter()}
        >
          <div>
            {/* Start Date */}
            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>Start Date</label>
              <Controller
                name='startDate'
                control={control}
                rules={{
                  required: "Start date is required",
                  validate: (value) =>
                    !value
                      ? "Start date is required"
                      : endDate && new Date(value) > new Date(endDate)
                      ? "Start date cannot be after end date"
                      : true,
                }}
                render={({ field, fieldState }) => (
                  <DateRangeWithRegister
                    isSingleDate
                    field={field}
                    fieldState={fieldState}
                    placeholder='Select Start Date'
                    showCalendarIcon
                  />
                )}
              />
              {errors?.startDate && (
                <span className='text-sm text-red-500'>
                  {errors.startDate.message}
                </span>
              )}
            </div>

            {/* End Date */}
            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>End Date</label>
              <Controller
                name='endDate'
                control={control}
                rules={{
                  required: "End date is required",
                  validate: (value) =>
                    !value
                      ? "End date is required"
                      : startDate && new Date(value) < new Date(startDate)
                      ? "End date cannot be earlier than start date"
                      : true,
                }}
                render={({ field, fieldState }) => (
                  <DateRangeWithRegister
                    isSingleDate
                    field={field}
                    fieldState={fieldState}
                    placeholder='Select End Date'
                    showCalendarIcon
                  />
                )}
              />
              {errors?.endDate && (
                <span className='text-sm text-red-500'>
                  {errors.endDate.message}
                </span>
              )}
            </div>

            {/* Details (optional) */}
            <div className='mt-1 col-span-2 sm:col-span-1'>
              <label className='did-floating-label'>
                Details <span className='text-gray-400'>(optional)</span>
              </label>
              <textarea
                className='did-floating-input capitalize'
                style={{ minHeight: "80px", resize: "vertical" }}
                placeholder='Add additional details'
                maxLength={500}
                {...register("details", {
                  maxLength: {
                    value: 500,
                    message: "Reason must be at most 500 characters",
                  },
                })}
              />
            </div>
            {errors?.details && (
              <span className='text-sm text-red-500 mt-1 block'>
                {errors.details.message}
              </span>
            )}
            <div className='text-right text-xs text-gray-500 mt-1'>
              {Math.max(0, 500 - (watch("details")?.length || 0))} characters
              remaining
            </div>
          </div>
        </ModalSkeleton>
      )}
    </>
  );
};

export default LeaveUpdateModal;
